/**
 * LiveScribePanel — Real-time encounter scribe UI for the Charts sidebar.
 *
 * Split-view layout (no tabs):
 *   Top    — Rolling transcript (auto-scroll, ~25% height)
 *   Middle — Progressive SOAP note (fills remaining space)
 *
 * After recording stops:
 *   Shows a processing loader with stage/percentage,
 *   then displays the final SOAP note with Level of Visit when complete.
 */
import React, { useState, useEffect, useRef } from "react";
import { FormattedNoteText, FormattedNoteList } from "../../../utils/noteFormat";
import { useSelector } from "react-redux";
import { useRecording } from "../../../context/RecordingContext";
import { htmlNoteToText, splitApEntries } from "../../../utils/apnote";
import { eventBus } from "../../../utils/eventBus";
import blinkscribeapi from "../../../api/blinkscribeapi";
import { classifyVisitRows, isNoteInFlight } from "../../../utils/recordingCompletion";
import { otherNotesOnVisit, otherNotesLine } from "../../../utils/otherUserNote";
import { NotePdfButton } from "./notePdf";

// Accepts 1-5, "3", "99213", "99204", "Level 3" — returns 1-5 or 0.
const normalizeLov = (raw) => {
    const n = parseInt(String(raw ?? "").replace(/[^0-9]/g, ""), 10);
    if (!n) return 0;
    if (n >= 99211 && n <= 99215) return n - 99210;
    if (n >= 99202 && n <= 99205) return n - 99200;
    return n >= 1 && n <= 5 ? n : 0;
};

// ─── Styles ──────────────────────────────────────────────────
const styles = {
    // one grey line: the visit has other notes than the one shown
    otherNotesLine: {
        fontSize: "11px",
        color: "#6B7280",
        marginBottom: "6px",
        lineHeight: 1.4,
    },
    panel: {
        display: "flex",
        flexDirection: "column",
        width: "100%",
        height: "100%",
        fontFamily: "'Inter', sans-serif",
    },
    sectionTitle: {
        fontSize: "10px",
        fontWeight: 700,
        color: "#1D2951",
        textTransform: "uppercase",
        letterSpacing: "0.05em",
        marginBottom: "4px",
        marginTop: "4px",
        display: "flex",
        alignItems: "center",
        gap: "6px",
    },
    transcriptWrap: {
        maxHeight: "140px",
        minHeight: "60px",
        overflowY: "auto",
        borderBottom: "1px solid #e5e7eb",
        padding: "4px 2px",
        fontSize: "12px",
        flexShrink: 0,
    },
    transcriptLine: {
        fontSize: "11px",
        color: "#374151",
        lineHeight: "1.5",
        whiteSpace: "pre-wrap",
        padding: "4px 0",
    },
    doctorLabel: {
        fontWeight: 700,
        color: "#1D2951",
    },
    patientLabel: {
        fontWeight: 700,
        color: "#E17954",
    },
    soapWrap: {
        flex: 1,
        overflowY: "auto",
        padding: "4px 2px",
        fontSize: "12.5px",
        // The sidebar container centers text; a clinical note must read
        // left-aligned (QA 2026-08-24: "SOAP note not displayed clearly").
        textAlign: "left",
    },
    soapSection: {
        marginBottom: "6px",
        textAlign: "left",
    },
    soapLabel: {
        fontSize: "11px",
        fontWeight: 700,
        color: "#4B5563",
        marginBottom: "2px",
        textAlign: "left",
    },
    soapText: {
        fontSize: "12.5px",
        color: "#374151",
        lineHeight: "1.4",
        whiteSpace: "pre-wrap",
        padding: "4px 6px",
        background: "#f9fafb",
        borderRadius: "4px",
        border: "1px solid #e5e7eb",
        textAlign: "left",
    },
    lovBar: {
        display: "flex",
        alignItems: "center",
        gap: "6px",
        padding: "6px 4px",
        borderTop: "1px solid #e5e7eb",
        flexShrink: 0,
    },
    emptyState: {
        textAlign: "center",
        color: "#9CA3AF",
        fontSize: "11px",
        padding: "24px 8px",
    },
    pulsingDot: {
        width: "6px",
        height: "6px",
        borderRadius: "50%",
        backgroundColor: "#EF4444",
        display: "inline-block",
        animation: "pulse 1.5s infinite ease-in-out",
    },
    liveIndicator: {
        display: "flex",
        alignItems: "center",
        fontSize: "10px",
        fontWeight: 600,
        color: "#EF4444",
        marginBottom: "4px",
        gap: "4px",
    },
    // Processing loader styles
    processingOverlay: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "32px 16px",
        flex: 1,
    },
    progressBarBg: {
        width: "100%",
        height: "6px",
        background: "#e5e7eb",
        borderRadius: "3px",
        overflow: "hidden",
        marginTop: "12px",
        marginBottom: "8px",
    },
    progressBarFill: {
        height: "100%",
        borderRadius: "3px",
        background: "linear-gradient(90deg, #6366f1, #4f46e5)",
        transition: "width 1s ease",
    },
    processingStage: {
        fontSize: "12px",
        fontWeight: 600,
        color: "#4B5563",
        marginTop: "4px",
    },
    processingPercent: {
        fontSize: "20px",
        fontWeight: 800,
        color: "#1D2951",
        marginBottom: "4px",
    },
};

// ─── Component ───────────────────────────────────────────────
const LiveScribePanel = ({ patientId, onNoteLoaded, appointmentDate, onLovChange, isNewPatient }) => {
    // Level 1-5 → displayed CPT: new-patient visits (Type of Visit = "New
    // Patient") bill 99202-05 (level-1 clamps to 99202 — 99201 was deleted
    // in 2021), everything else 99211-15. Stored value stays 1-5.
    const lovCpt = (n) => {
        const lvl = Number(n) || 0;
        return isNewPatient ? 99200 + Math.max(2, lvl) : 99210 + lvl;
    };
    const transcriptRef = useRef(null);

    // Persisted note from API (for when chart is reopened)
    const [fetchedNote, setFetchedNote] = useState(null);
    // The visit date already has a recording row but no finished note yet
    // ({ status }): show "Note processing…" instead of an older visit's note.
    const [pendingNote, setPendingNote] = useState(null);
    // Other finished notes of the shown note's visit (two users' notes on one
    // visit: BlinkScribe no longer merges them silently, 10-06). This panel
    // shows one note; say that the others exist.
    const [visitOthers, setVisitOthers] = useState([]);
    // Bumped while pendingNote is in flight so the panel re-checks on its own
    // (e.g. the chart was reopened mid-processing).
    const [pendingTick, setPendingTick] = useState(0);


    const {
        isGlobalRecording,
        liveSOAPNote,
        liveTranscript,
        toolCallCount,
        liveScribeHealthy,
        // Post-recording
        isProcessing,
        processingStage,
        processingPercent,
        lastSOAPNote,
        lastTranscript,
        lastNotePatientId,
        lastCompletedRecording,
        patientInfo: recordingPatientInfo,
    } = useRecording();

    // Only show post-recording data if it belongs to THIS patient
    const noteMatchesPatient = !lastNotePatientId || String(lastNotePatientId) === String(patientId);
    // Processing THIS chart's recording. Another patient's recording still
    // processing (provider switched charts mid-recording → auto-save) must not
    // keep this chart's panel blank: the completion poll now waits for the
    // finished note (up to 10 min), not 120 s.
    const processingHere = isProcessing && noteMatchesPatient;
    // Id of the note the recording that just finished produced — only when it
    // is THIS patient's (RecordingContext resolves it from the recording's own
    // row; never a note cached before the recording started).
    const completedRecordingId =
        lastCompletedRecording?.id && String(lastCompletedRecording.patientId) === String(patientId)
            ? lastCompletedRecording.id
            : null;

    // A recording starting for this patient invalidates whatever note the
    // panel cached at chart-open — typically the previous visit's note from
    // the any-date fallback. Clearing it lets the fetch effect below pick up
    // today's row once processing finishes (its guard skipped the refetch
    // while a cached id existed) and keeps the old id away from every Edit
    // button.
    useEffect(() => {
        if (!isGlobalRecording) return;
        const recPid = recordingPatientInfo?.patientid;
        if (recPid && String(recPid) !== String(patientId)) return;
        setFetchedNote(null);
        setPendingNote(null);
    }, [isGlobalRecording, recordingPatientInfo?.patientid, patientId]);

    // Surface the note's Level of Visit to other tabs (CareIQ shows it as a
    // chip on the VISIT order row) — after a recording the sidebar selector
    // is below the note and easy to miss. Must sit AFTER the useRecording()
    // destructure: referencing lastSOAPNote above it crashed the chart
    // (TDZ ReferenceError, blank screen).
    useEffect(() => {
        const n = normalizeLov(fetchedNote?.lov ?? lastSOAPNote?.level_of_visit);
        if (n) eventBus.emit("noteLovLoaded", n);
        // Preventive/DOT visits carry a visit_class instead of an E/M level;
        // the sidebar listens and suggests the matching Type of Visit.
        if (fetchedNote?.visitClass) {
            eventBus.emit("noteVisitClassLoaded", {
                cls: fetchedNote.visitClass,
                seq: fetchedNote.wellnessSequence || null,
            });
        }
    }, [fetchedNote, lastSOAPNote]);

    const orders = useSelector((state) => state.orders.orders);
    const lovFromOrders = orders?.lovtov?.[0]?.lov ?? orders?.lov ?? 0;
    const lovFromDump = useSelector(
        (state) => state.dump.storedvalues.find((item) => item.id === "lov")?.value
    );
    const lovValue = Number(lovFromOrders || lovFromDump) || 0;

    // Auto-scroll transcript
    useEffect(() => {
        if (transcriptRef.current) {
            transcriptRef.current.scrollTop = transcriptRef.current.scrollHeight;
        }
    }, [liveTranscript]);

    // ── Fetch today's existing BS note on mount (for note persistence) ──
    useEffect(() => {
        if (!patientId || isGlobalRecording || processingHere) return;
        // Even if we have lastSOAPNote (live data after recording stop), still
        // fetch when fetchedNote.recordingId is missing — the Edit button
        // depends on having that ID and live SOAP doesn't carry it.
        if (lastSOAPNote && Object.keys(lastSOAPNote).length > 0 && fetchedNote?.recordingId) return;

        let cancelled = false;
        const fetchExisting = async () => {
            try {
                // Use the appointment's date if provided, else fall back to today
                let filterDate;
                if (appointmentDate) {
                    const d = new Date(appointmentDate);
                    filterDate = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
                } else {
                    const now = new Date();
                    filterDate = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')}`;
                }
                const res = await blinkscribeapi.post(
                    `/v1/patient/${patientId}/recording/details`,
                    { filter_array: { date_filter: { start_date: filterDate, end_date: filterDate } } }
                );
                if (cancelled) return;
                let records = res?.data || res;
                if (!Array.isArray(records)) records = [];
                const visit = classifyVisitRows(records);
                let usable = visit.usable;
                const visitDayRows = records;

                // The visit date already has a row (Submitted / Processing /
                // any other state) but no finished note: that recording IS
                // this visit's note, still being produced. Never fall back to
                // an older visit's note here — it became the Edit target and
                // providers saved into last visit's note (stale-note fix
                // 2026-09-28).
                if (visit.state === "pending") {
                    setFetchedNote(null);
                    setVisitOthers([]);
                    // Keep the same object while the status is unchanged: a
                    // new object on every re-check restarted the re-check
                    // interval below and reset its 10-minute cap (it polled
                    // forever for a row that never finishes).
                    setPendingNote((prev) =>
                        prev && prev.status === visit.pendingStatus ? prev : { status: visit.pendingStatus });
                    return;
                }
                setPendingNote(null);

                let fromFallback = false;
                // No row at all for the opened visit date — the chart was
                // opened from search or a different day's context (bmalik/
                // Ferdoos 2026-09-01: 08/26 note looked "gone"). Fall back to
                // the patient's most recent note; the render header already
                // labels non-today notes with their own date.
                if (usable.length === 0) {
                    const now = new Date();
                    const todayStr = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')}`;
                    const fbRes = await blinkscribeapi.post(
                        `/v1/patient/${patientId}/recording/details`,
                        { filter_array: { date_filter: { start_date: "2020-01-01", end_date: todayStr } }, limit: 10 }
                    );
                    if (cancelled) return;
                    const fbRecords = fbRes?.data || fbRes;
                    if (Array.isArray(fbRecords)) {
                        usable = fbRecords.filter(
                            r => r.transcriptionstatus === "Summarized" || r.transcriptionstatus === "APPROVED"
                        );
                        fromFallback = true;
                    }
                }
                if (usable.length === 0) return;

                // Take the latest Summarized or APPROVED record with real note
                // content. A later *_combine_edit row can be status-complete
                // while still missing note_json; don't let that shadow the
                // original LiveScribe note.
                const sorted = usable
                    .sort((a, b) => new Date(b.ts) - new Date(a.ts));
                for (const latest of sorted) {
                    if (!latest?.id) continue;

                    // Fetch full note content
                    const noteRes = await blinkscribeapi.get(`/v1/recording/note/${latest.id}/get`);
                    if (cancelled) return;
                    const noteData = noteRes?.data?.[0] || noteRes?.[0] || noteRes?.data || noteRes;
                    const nj = typeof noteData?.note_json === 'string' ? JSON.parse(noteData.note_json) : noteData?.note_json;

                    console.log("[LSP] noteData keys:", noteData ? Object.keys(noteData) : "NULL");
                    console.log("[LSP] note_json:", nj ? (typeof nj === 'object' ? `keys: ${Object.keys(nj)}` : typeof nj) : "NULL");

                    // Handle multiple formats:
                    //   1. LiveScribe checkpoint: note_json.soap_note = { chief_complaint, hpi, ... }
                    //   2. Final note_json:       note_json = { chief_complaint, hpi, ..., level_of_visit }
                    //   3. Top-level SOAP fields: noteData.subjective, noteData.objective, etc.
                    let soap = nj?.soap_note || (nj?.chief_complaint ? nj : null);

                    // Fallback: build soap from top-level fields
                    if (!soap && (noteData?.subjective || noteData?.objective || noteData?.assessment || noteData?.plan)) {
                        soap = {};
                        if (noteData.subjective) soap.chief_complaint = noteData.subjective;
                        if (noteData.objective) soap.physical_examination = noteData.objective;
                        if (noteData.assessment) soap.assessment_plan = noteData.assessment;
                        if (noteData.plan) soap.doctor_instructions = noteData.plan;
                    }

                    console.log("[LSP] soap found:", !!soap);
                    if (!soap) continue;

                    setVisitOthers(fromFallback ? [] : otherNotesOnVisit(visitDayRows, latest));
                    setFetchedNote({
                        soap,
                        recordingId: latest.id,
                        // The note PDF's name (NotePdfButton, 7825).
                        audiolink: latest.audiolink || null,
                        date: latest.ts || appointmentDate || null,
                        lov: nj?.level_of_visit || soap?.level_of_visit || null,
                        lovReasoning: nj?.level_of_visit_reasoning || soap?.level_of_visit_reasoning || "",
                        visitClass: nj?.visit_class || null,
                        wellnessSequence: nj?.wellness_sequence || null,
                        icd10: nj?.suggested_icd10 || [],
                        orders: nj?.suggested_orders || [],
                    });
                    if (onNoteLoaded) onNoteLoaded(true);
                    break;
                }
            } catch (e) {
                // Non-blocking
                console.warn("[LiveScribePanel] Failed to fetch existing note:", e?.message);
            }
        };
        fetchExisting();
        // RecordingContext emits 'refreshDashboard' when the polling worker
        // detects transcription is complete. Re-fetch on that signal so the
        // Edit button's recordingId becomes available without waiting for a
        // remount.
        const handleRefresh = () => fetchExisting();
        eventBus.on("refreshDashboard", handleRefresh);
        return () => {
            cancelled = true;
            eventBus.off("refreshDashboard", handleRefresh);
        };
    }, [patientId, isGlobalRecording, processingHere, lastSOAPNote, appointmentDate, fetchedNote?.recordingId, pendingTick]);

    // While the visit's note is still being produced, re-check every 15 s
    // (up to 10 min) so "Note processing…" turns into the note by itself.
    useEffect(() => {
        if (!pendingNote || !isNoteInFlight(pendingNote.status) || isGlobalRecording || processingHere) return;
        let n = 0;
        const t = setInterval(() => {
            n += 1;
            if (n > 40) { clearInterval(t); return; }
            setPendingTick((x) => x + 1);
        }, 15000);
        return () => clearInterval(t);
    }, [pendingNote, isGlobalRecording, processingHere]);

    // No auto-open when a recording finishes: the panel reloads the finished
    // note on its own (refreshDashboard + the processing-ended refetch above)
    // and its Edit button opens the editor. Auto-opening popped a modal over
    // whatever the user was doing, and "No document found" when the note had
    // no sections (owner, 2026-09-29).

    // ── Format transcript with speaker labels ──
    const renderTranscript = (text) => {
        if (!text?.trim()) {
            return (
                <div style={styles.emptyState}>
                    <div style={{ fontSize: "20px", marginBottom: "6px" }}>🎙️</div>
                    Listening to the encounter...
                    <br />
                    Transcript will appear here as the conversation progresses.
                </div>
            );
        }

        // Pre-process: split on speaker labels that appear mid-line
        const normalized = text.replace(
            /(?<=\S)\s+(?=(Doctor|Dr|Provider|Patient|Pt|Other|Nurse|Staff)\s*:)/gi,
            "\n"
        );

        return normalized.split("\n").filter(l => l.trim()).map((rawLine, i) => {
            const line = rawLine.trim();
            const lowerLine = line.toLowerCase();

            let labelStyle = {};
            let borderColor = "transparent";
            if (lowerLine.startsWith("doctor:") || lowerLine.startsWith("dr:") || lowerLine.startsWith("provider:")) {
                labelStyle = styles.doctorLabel;
                borderColor = "#1D2951";
            } else if (lowerLine.startsWith("patient:") || lowerLine.startsWith("pt:")) {
                labelStyle = styles.patientLabel;
                borderColor = "#E17954";
            } else if (lowerLine.startsWith("other:") || lowerLine.startsWith("nurse:") || lowerLine.startsWith("staff:")) {
                labelStyle = { fontWeight: 700, color: "#6B7280" };
                borderColor = "#6B7280";
            }

            const colonIdx = line.indexOf(":");
            if (colonIdx > 0 && colonIdx < 20 && labelStyle.fontWeight) {
                const speaker = line.substring(0, colonIdx + 1);
                const rest = line.substring(colonIdx + 1);
                return (
                    <div key={i} style={{
                        ...styles.transcriptLine,
                        borderLeft: `3px solid ${borderColor}`,
                        paddingLeft: "8px",
                        marginBottom: "4px",
                    }}>
                        <span style={labelStyle}>{speaker}</span>
                        {rest}
                    </div>
                );
            }
            return (
                <div key={i} style={styles.transcriptLine}>
                    {line}
                </div>
            );
        });
    };

    // ── SOAP sections display labels ──
    const SOAP_LABELS = {
        chief_complaint: "Chief Complaint",
        hpi: "History of Present Illness",
        review_of_systems: "Review of Systems",
        physical_examination: "Physical Examination",
        assessment_plan: "Assessment & Plan",
        current_medications: "Current Medications",
        allergies: "Allergies",
        doctor_instructions: "Doctor Instructions",
    };

    // ── Render SOAP note (works for both live and post-recording) ──
    const renderSOAP = (soapNote) => {
        if (!soapNote || Object.keys(soapNote).length === 0) {
            return (
                <div style={styles.emptyState}>
                    <div style={{ fontSize: "20px", marginBottom: "6px" }}>📋</div>
                    SOAP note will build progressively
                    <br />
                    as clinical facts are identified.
                </div>
            );
        }

        return Object.entries(SOAP_LABELS).map(([key, label]) => {
            const value = soapNote[key];
            if (!value) return null;

            // Flatten array items — each item may be a string OR an object
            const formatItem = (item) => {
                if (typeof item === "string") return item;
                if (typeof item === "object" && item !== null) {
                    // Common shapes: { diagnosis, plan }, { condition, recommendation }, etc.
                    const parts = [];
                    const diag = item.diagnosis || item.condition || item.problem || item.assessment || item.name || "";
                    const plan = item.plan || item.recommendation || item.treatment || item.note || "";
                    if (diag) parts.push(diag);
                    if (plan) parts.push(plan);
                    if (parts.length > 0) return parts.join(" — ");
                    // Fallback: join all non-empty string values
                    const vals = Object.values(item).filter(v => typeof v === "string" && v.trim());
                    return vals.length > 0 ? vals.join(" — ") : JSON.stringify(item);
                }
                return String(item);
            };

            // htmlNoteToText keeps paragraph breaks (a bare tag-strip ran the
            // whole A&P together); splitApEntries breaks single-blob A&Ps into
            // one card per diagnosis (QA 2026-08-20).
            const stripHtml = (str) => typeof str === "string" ? htmlNoteToText(str) : str;
            const listItems = Array.isArray(value)
                ? (key === "assessment_plan"
                    ? splitApEntries(value.map(formatItem))
                    : value.map((v) => stripHtml(formatItem(v))))
                : null;

            return (
                <div key={key} style={styles.soapSection}>
                    <div style={styles.soapLabel}>{label}</div>
                    <div style={styles.soapText}>
                        {Array.isArray(value) ? (
                            <FormattedNoteList
                                items={listItems}
                                numbered={key === "assessment_plan"}
                            />
                        ) : (
                            <FormattedNoteText text={stripHtml(typeof value === "object" ? formatItem(value) : value)} />
                        )}
                    </div>
                </div>
            );
        });
    };

    // ── Render Level of Visit bar (post-recording only). Rendered ABOVE
    //    the SOAP sections and styled as a card with the CPT code — at the
    //    bottom of a long note it wasn't visible without scrolling
    //    (QA 2026-08-24: "Level of visit is not seen clearly"). ──
    const renderLovBar = (lov, reasoning) => {
        const lovNum = normalizeLov(lov);
        // Visual rule: orange = provider-set (persisted on the appointment),
        // blue = AI suggestion only. The AI value never overwrites a provider
        // selection — with nothing saved the AI number shows as the active
        // (blue) level, and clicking it adopts/persists it.
        // No AI level (preventive/DOT classification, legacy note, failed
        // estimation) still renders the bar — the provider must always be
        // able to see the saved level and set one (Jessica Blanton 09/01:
        // wellness-classified note left NO way to set a level at all).
        const saved = (lovValue >= 1 && lovValue <= 5) ? lovValue : null;
        const effective = saved || lovNum;
        return (
            <div style={{ ...styles.lovBar, borderTop: "none", background: "#FDF6F3", border: "1px solid #F5DED4", borderRadius: "8px", padding: "7px 8px", marginBottom: "6px" }}>
                <span style={{ fontSize: "11px", fontWeight: 800, color: "#1D2951", whiteSpace: "nowrap" }}>
                    Level of Visit
                </span>
                {effective ? (
                <span title={saved ? "Set by provider" : "AI-suggested — click a number to accept or override"} style={{ fontSize: "12px", fontWeight: 800, color: "#fff", background: saved ? "#E17954" : "#3B82F6", borderRadius: "6px", padding: "2px 8px", whiteSpace: "nowrap" }}>
                    {lovCpt(effective)}
                </span>
                ) : null}
                {[1, 2, 3, 4, 5].map((n) => {
                    const isProviderSet = saved === n;
                    const isAiSuggested = lovNum === n;
                    // Provider can override the AI level — clicking a different
                    // number shows the AI's justification in a confirmation
                    // dialog first (doctor request 2026-08-24). Clicking the AI
                    // number itself (nothing saved) accepts and persists it.
                    const handleOverride = () => {
                        if (n === saved || !onLovChange) return;
                        const newCpt = lovCpt(n);
                        const msg = lovNum
                            ? (n === lovNum
                                ? `Accept AI-suggested ${lovCpt(lovNum)}`
                                : `AI suggested ${lovCpt(lovNum)}`) +
                              (reasoning ? `:\n"${reasoning}"\n\n` : ".\n\n") +
                              (n === lovNum
                                ? `Set Level of Visit to ${newCpt}?`
                                : `Override Level of Visit to ${newCpt}?`)
                            : `The AI didn't assign an E/M level for this visit` +
                              (reasoning ? `:\n"${reasoning}"\n\n` : ".\n\n") +
                              `Set Level of Visit to ${newCpt}?`;
                        // true = this dialog already confirmed; don't let the
                        // sidebar's no-AI confirm ask a second time.
                        if (window.confirm(msg)) onLovChange(n, true);
                    };
                    return (
                        <div
                            key={n}
                            onClick={handleOverride}
                            title={isProviderSet ? "Set by provider" : isAiSuggested ? `AI suggests: ${reasoning}` : `Set Level of Visit to ${lovCpt(n)}`}
                            style={{
                                width: "24px",
                                height: "24px",
                                borderRadius: "6px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontWeight: 700,
                                fontSize: "10px",
                                background: isProviderSet ? "#E17954" : isAiSuggested ? (saved ? "#EFF6FF" : "#3B82F6") : "#fff",
                                color: isProviderSet ? "#fff" : isAiSuggested ? (saved ? "#1D4ED8" : "#fff") : "#1D2951",
                                border: isAiSuggested && !isProviderSet ? "1.5px solid #93C5FD" : "1px solid #e5e7eb",
                                boxShadow: "0 0 3px rgba(0,0,0,0.06)",
                                position: "relative",
                                cursor: onLovChange && n !== saved ? "pointer" : "default",
                            }}
                        >
                            {n}
                            {isAiSuggested && !isProviderSet && (
                                <span style={{
                                    position: "absolute",
                                    top: "-6px",
                                    right: "-6px",
                                    fontSize: "7px",
                                    fontWeight: 800,
                                    color: "#fff",
                                    background: "#3B82F6",
                                    borderRadius: "3px",
                                    padding: "0px 2px",
                                    lineHeight: "12px",
                                    letterSpacing: "0.02em",
                                }}>
                                    AI
                                </span>
                            )}
                        </div>
                    );
                })}
                {/* Inline justification removed — it now appears in the
                    override confirmation dialog instead. */}
            </div>
        );
    };

    // ─── POST-RECORDING: Processing Loader ───────────────────
    if (isProcessing && noteMatchesPatient) {
        return (
            <div style={styles.panel}>
                <style>{`
                    @keyframes pulse {
                      0%, 100% { opacity: 1; transform: scale(1); }
                      50% { opacity: 0.4; transform: scale(0.8); }
                    }
                    @keyframes spin {
                      to { transform: rotate(360deg); }
                    }
                `}</style>

                <div style={{ ...styles.liveIndicator, color: "#6366f1" }}>
                    <span style={{
                        width: "6px", height: "6px", borderRadius: "50%",
                        backgroundColor: "#6366f1", display: "inline-block",
                        animation: "pulse 1.5s infinite ease-in-out"
                    }}></span>
                    PROCESSING
                </div>

                <div style={styles.processingOverlay}>
                    {/* Spinner icon */}
                    <div style={{
                        width: "32px", height: "32px", border: "3px solid #e5e7eb",
                        borderTopColor: "#6366f1", borderRadius: "50%",
                        animation: "spin 0.8s linear infinite", marginBottom: "12px"
                    }}></div>

                    <div style={styles.processingPercent}>{processingPercent}%</div>
                    <div style={styles.processingStage}>{processingStage}…</div>

                    <div style={styles.progressBarBg}>
                        <div style={{ ...styles.progressBarFill, width: `${processingPercent}%` }}></div>
                    </div>
                </div>

                {/* Show the last known SOAP note (read-only) while processing */}
                {lastSOAPNote && Object.keys(lastSOAPNote).length > 0 && (
                    <>
                        <div style={{ ...styles.sectionTitle, opacity: 0.6 }}>
                            <span>📋</span> Last SOAP (updating…)
                        </div>
                        <div style={{ ...styles.soapWrap, opacity: 0.5, maxHeight: "200px" }}>
                            {renderSOAP(lastSOAPNote)}
                        </div>
                    </>
                )}
            </div>
        );
    }

    // ─── POST-RECORDING: Show final SOAP note (no longer recording or processing) ───
    // Prefer the fetched (final) note over the stale live SOAP — the live note
    // is built incrementally during streaming and may contain preliminary
    // diagnoses the final summarizer corrected.
    if (!isGlobalRecording && noteMatchesPatient && lastSOAPNote && Object.keys(lastSOAPNote).length > 0) {
        const displaySOAP = fetchedNote?.soap || lastSOAPNote;
        const displayLoV = fetchedNote?.soap ? fetchedNote.lov : lastSOAPNote?.level_of_visit;
        const displayLoVReasoning = fetchedNote?.soap ? fetchedNote.lovReasoning : lastSOAPNote?.level_of_visit_reasoning;
        return (
            <div style={styles.panel}>
                <div style={{
                    display: "flex", alignItems: "center", gap: "6px",
                    fontSize: "10px", fontWeight: 600, color: "#059669", marginBottom: "4px"
                }}>
                    <span>✅</span> ENCOUNTER COMPLETE
                    {(fetchedNote?.recordingId || completedRecordingId) ? (
                    <button
                        onClick={() => eventBus.emit("openNoteEditor", { recordingId: fetchedNote?.recordingId || completedRecordingId })}
                        style={{
                            marginLeft: "auto", fontSize: "10px", fontWeight: 600,
                            color: "#E17954", background: "none", border: "1px solid #E17954",
                            borderRadius: "4px", padding: "2px 8px", cursor: "pointer",
                        }}
                    >
                        ✏️ Edit
                    </button>
                    ) : (
                    // Today's final note isn't available yet — there is nothing
                    // correct to edit (an older id here is what opened last
                    // visit's note).
                    <span style={{ marginLeft: "auto", fontSize: "10px", fontWeight: 500, color: "#6B7280" }}>
                        Note processing…
                    </span>
                    )}
                    {/* 7825: the note PDF to print (null elsewhere). */}
                    <NotePdfButton audiolink={fetchedNote?.recordingId ? fetchedNote.audiolink : null} />
                </div>
                {fetchedNote?.recordingId && otherNotesLine(visitOthers) && (
                    <div style={styles.otherNotesLine}>{otherNotesLine(visitOthers)}</div>
                )}

                {renderLovBar(displayLoV, displayLoVReasoning)}
                {/* Preventive/DOT visits have no E/M level — show the
                    AI-detected visit class instead of the 1-5 bar. */}
                {!normalizeLov(displayLoV) && fetchedNote?.visitClass && fetchedNote.visitClass !== "em" && (
                    <div style={{ ...styles.lovBar, borderTop: "none", background: "#FDF6F3", border: "1px solid #F5DED4", borderRadius: "8px", padding: "7px 8px", marginBottom: "6px" }}>
                        <span style={{ fontSize: "11px", fontWeight: 800, color: "#1D2951", whiteSpace: "nowrap" }}>
                            Visit Type
                        </span>
                        <span style={{ fontSize: "12px", fontWeight: 800, color: "#fff", background: "#3B82F6", borderRadius: "6px", padding: "2px 8px", whiteSpace: "nowrap", position: "relative" }}>
                            {fetchedNote.visitClass === "dot_physical"
                                ? "DOT Physical"
                                : (fetchedNote.visitClass === "wellness_medicare" ? "Wellness Medicare" : "Wellness Commercial")
                                  + (fetchedNote.wellnessSequence ? ` — ${fetchedNote.wellnessSequence === "first" ? "First" : "Subsequent"}` : "")}
                            <span style={{ position: "absolute", top: "-6px", right: "-6px", fontSize: "7px", fontWeight: 800, color: "#fff", background: "#1D4ED8", borderRadius: "3px", padding: "0px 2px", lineHeight: "12px" }}>AI</span>
                        </span>
                    </div>
                )}
                <div style={styles.sectionTitle}>
                    <span>📋</span> SOAP Note
                </div>
                <div style={styles.soapWrap}>
                    {renderSOAP(displaySOAP)}
                </div>
            </div>
        );
    }

    // ─── PERSISTED NOTE: Show existing note from API when chart is reopened ───
    if (!isGlobalRecording && fetchedNote?.soap) {
        // The note shown here is for the appointment's date (or today). When
        // there's no note for today we fall back to an older one, so always
        // surface the date and only say "TODAY'S" when it really is today —
        // otherwise it's unclear which encounter's note is on screen.
        const noteDate = fetchedNote?.date ? new Date(fetchedNote.date) : null;
        const noteDateValid = noteDate && !isNaN(noteDate.getTime());
        const isToday = noteDateValid && noteDate.toDateString() === new Date().toDateString();
        const noteDateStr = noteDateValid
            ? noteDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
            : '';
        return (
            <div style={styles.panel}>
                <div style={{
                    display: "flex", alignItems: "center", gap: "6px",
                    fontSize: "10px", fontWeight: 600, color: "#059669", marginBottom: "4px"
                }}>
                    <span>📝</span> {isToday ? "TODAY'S NOTE" : "NOTE"}
                    {noteDateStr && (
                        <span style={{ fontWeight: 500, color: "#6B7280" }}>· {noteDateStr}</span>
                    )}
                    <button
                        onClick={() => eventBus.emit("openNoteEditor", { recordingId: fetchedNote?.recordingId || completedRecordingId })}
                        style={{
                            marginLeft: "auto", fontSize: "10px", fontWeight: 600,
                            color: "#E17954", background: "none", border: "1px solid #E17954",
                            borderRadius: "4px", padding: "2px 8px", cursor: "pointer",
                        }}
                    >
                        ✏️ Edit
                    </button>
                    {/* 7825: the note PDF to print (null elsewhere). */}
                    <NotePdfButton audiolink={fetchedNote?.audiolink} />
                </div>
                {otherNotesLine(visitOthers) && (
                    <div style={styles.otherNotesLine}>{otherNotesLine(visitOthers)}</div>
                )}

                {renderLovBar(fetchedNote.lov, fetchedNote.lovReasoning)}
                {/* Preventive/DOT visits have no E/M level — surface the
                    AI-detected visit class here too (the post-recording
                    branch already does; without this a reopened wellness
                    chart showed nothing at all). */}
                {!normalizeLov(fetchedNote.lov) && fetchedNote?.visitClass && fetchedNote.visitClass !== "em" && (
                    <div style={{ ...styles.lovBar, borderTop: "none", background: "#FDF6F3", border: "1px solid #F5DED4", borderRadius: "8px", padding: "7px 8px", marginBottom: "6px" }}>
                        <span style={{ fontSize: "11px", fontWeight: 800, color: "#1D2951", whiteSpace: "nowrap" }}>
                            Visit Type
                        </span>
                        <span style={{ fontSize: "12px", fontWeight: 800, color: "#fff", background: "#3B82F6", borderRadius: "6px", padding: "2px 8px", whiteSpace: "nowrap", position: "relative" }}>
                            {fetchedNote.visitClass === "dot_physical"
                                ? "DOT Physical"
                                : (fetchedNote.visitClass === "wellness_medicare" ? "Wellness Medicare" : "Wellness Commercial")
                                  + (fetchedNote.wellnessSequence ? ` — ${fetchedNote.wellnessSequence === "first" ? "First" : "Subsequent"}` : "")}
                            <span style={{ position: "absolute", top: "-6px", right: "-6px", fontSize: "7px", fontWeight: 800, color: "#fff", background: "#1D4ED8", borderRadius: "3px", padding: "0px 2px", lineHeight: "12px" }}>AI</span>
                        </span>
                    </div>
                )}
                <div style={styles.sectionTitle}>
                    <span>📋</span> SOAP Note
                </div>
                <div style={styles.soapWrap}>
                    {renderSOAP(fetchedNote.soap)}
                </div>
            </div>
        );
    }

    // ─── PENDING NOTE: the visit already has a recording whose note isn't
    //     finished. Say so instead of showing (and offering to edit) an older
    //     visit's note. ───
    if (!isGlobalRecording && !processingHere && pendingNote) {
        const inFlight = isNoteInFlight(pendingNote.status);
        return (
            <div style={styles.panel}>
                <div style={{
                    display: "flex", alignItems: "center", gap: "6px",
                    fontSize: "10px", fontWeight: 600, color: "#6366f1", marginBottom: "4px"
                }}>
                    <span>📝</span> {inFlight ? "Note processing…" : "Note not ready"}
                </div>
                <div style={{ ...styles.emptyState, padding: "12px 8px" }}>
                    {inFlight
                        ? "This visit's recording is still being processed. The note will appear here when it's ready."
                        : `This visit's recording has no finished note yet${pendingNote.status ? ` (status: ${pendingNote.status})` : ""}.`}
                </div>
            </div>
        );
    }

    // ─── No recording and no post-recording data → don't render ───
    if (!isGlobalRecording) return null;

    // ─── LIVE RECORDING ──────────────────────────────────────
    return (
        <div style={styles.panel}>
            {/* CSS keyframes for pulsing dot */}
            <style>{`
                @keyframes pulse {
                  0%, 100% { opacity: 1; transform: scale(1); }
                  50% { opacity: 0.4; transform: scale(0.8); }
                }
            `}</style>

            {/* Live indicator */}
            <div style={styles.liveIndicator}>
                <span style={styles.pulsingDot}></span>
                LIVE SCRIBE
                {toolCallCount > 0 && (
                    <span style={{ color: "#6B7280", fontWeight: 400, marginLeft: "auto" }}>
                        {toolCallCount} update{toolCallCount !== 1 ? "s" : ""}
                    </span>
                )}
            </div>

            {/* LiveScribe Unhealthy Banner */}
            {!liveScribeHealthy && (
                <div style={{
                    background: "linear-gradient(135deg, #FEF3C7, #FDE68A)",
                    border: "1px solid #F59E0B",
                    borderRadius: 8,
                    padding: "10px 12px",
                    margin: "4px 0 8px",
                    fontSize: 11,
                    lineHeight: 1.5,
                    color: "#92400E",
                }}>
                    <div style={{ fontWeight: 700, marginBottom: 3, display: "flex", alignItems: "center", gap: 6 }}>
                        <span style={{ fontSize: 14 }}>⚠️</span> LiveScribe Unavailable
                    </div>
                    <div>Real-time transcription is temporarily offline. <strong>Your recording is being saved locally</strong> and will be submitted automatically when the server is back up.</div>
                </div>
            )}

            {/* Rolling Transcript (top, fixed height) */}
            <div style={styles.sectionTitle}>
                <span>🎙</span> Transcript
            </div>
            <div style={styles.transcriptWrap} ref={transcriptRef}>
                {renderTranscript(liveTranscript)}
            </div>

            {/* SOAP Note (fills remaining space) */}
            <div style={styles.sectionTitle}>
                <span>📋</span> SOAP Note
            </div>
            <div style={styles.soapWrap}>
                {renderSOAP(liveSOAPNote)}
            </div>
        </div>
    );
};

export default LiveScribePanel;
